import {Component, Inject, isDevMode, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {Database, get, ref, runTransaction} from "@angular/fire/database";
import {MatSnackBar} from "@angular/material/snack-bar";
import {firstValueFrom} from "rxjs";
import {environment} from "../../../../environments/environment";
import statesdata from "../../../../assets/json/states.json";
import {States} from "../../../interfaces/States";
import {LogService} from "../../../services/log.service";

export interface AddedDonation {
  id: string;
  receipt_id: string;
}

@Component({
  selector: 'app-add-razorpay-donation',
  templateUrl: './add-razorpay-donation.component.html',
  styleUrls: ['./add-razorpay-donation.component.scss']
})
export class AddRazorpayDonationComponent implements OnInit {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  stateslist = statesdata as States[];
  //* TEMP (test run): in dev mode the donation goes to the test collection, but the donor form is read from the
  //* real attemptedDonations (read-only) and the email is replaced so a receipt can never reach the real donor.
  isTestRun = isDevMode();
  testEmail = 'gauravdahale@icloud.com';
  private attemptedCollection = this.isTestRun ? environment.productionMode.attemptedDonations : this.env.attemptedDonations;
  //* attemptedDonations records that look like they belong to this payment
  candidates: any[] = [];
  selectedCandidate: any = null;
  isLoading = true;
  isSaving = false;
  nextReceiptId = '';
  paymentDate: Date;
  amount: number;
  notes: any;

  myForm = new FormGroup({
    donated_by: new FormControl('', Validators.required),
    mobile: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{10}$')]),
    email: new FormControl('', [Validators.required, Validators.email]),
    pan: new FormControl('', [Validators.required, Validators.pattern('[A-Z]{5}[0-9]{4}[A-Z]{1}')]),
    address: new FormControl('', Validators.required),
    city: new FormControl('', Validators.required),
    state: new FormControl('', Validators.required),
    zipcode: new FormControl('', [Validators.required, Validators.maxLength(6)]),
    groupkey: new FormControl('GF', Validators.required),
    description: new FormControl('General Fund', Validators.required),
  });

  constructor(@Inject(MAT_DIALOG_DATA) public payment: any,
              private readonly dialogRef: MatDialogRef<AddRazorpayDonationComponent, AddedDonation>,
              private readonly mFirestore: AngularFirestore,
              private readonly mDatabase: Database,
              private readonly snackBar: MatSnackBar,
              private readonly mLogService: LogService) {
    // Razorpay sends notes as [] when the payment has none
    this.notes = Array.isArray(payment.notes) ? {} : (payment.notes || {});
    this.paymentDate = new Date(payment.created_at * 1000);
    this.amount = payment.amount / 100;
  }

  async ngOnInit() {
    this.myForm.patchValue({
      mobile: this.last10(this.notes.mobile || this.payment.contact),
      email: this.isTestRun ? this.testEmail : (this.notes.email || this.payment.email || ''),
      groupkey: this.notes.group || 'GF',
      description: this.notes.desc || this.notes.group || 'General Fund',
    });
    try {
      await Promise.all([this.loadCandidates(), this.previewReceiptId()]);
      if (this.candidates.length) this.applyCandidate(this.candidates[0]);
    } catch (e) {
      console.error('Error loading attempted donations:', e);
    }
    this.isLoading = false;
  }

  //* The website saves the donor form to attemptedDonations just before opening Razorpay checkout,
  //* so the matching record sits within a few hours of the payment and shares its mobile/email.
  private async loadCandidates() {
    const windowMs = 3 * 60 * 60 * 1000;
    const from = new Date(this.paymentDate.getTime() - windowMs);
    const to = new Date(this.paymentDate.getTime() + windowMs);
    const snap = await firstValueFrom(this.mFirestore.collection(this.attemptedCollection, r =>
      r.where('date', '>=', from).where('date', '<=', to)).get());

    const mobiles = [this.notes.mobile, this.payment.contact].map(m => this.last10(m)).filter(m => !!m);
    const emails = [this.notes.email, this.payment.email].map(e => (e || '').trim().toLowerCase()).filter(e => !!e);
    const gap = (a: any) => Math.abs(a.date.toDate().getTime() - this.paymentDate.getTime());
    const sameAmount = (a: any) => Number(a.amount) === this.amount;

    this.candidates = snap.docs
      .map(d => ({id: d.id, ...(d.data() as any)}))
      .filter(a => mobiles.includes(this.last10(a.donorMobile)) || emails.includes((a.email || '').trim().toLowerCase()))
      .sort((a, b) => (Number(sameAmount(b)) - Number(sameAmount(a))) || (gap(a) - gap(b)));
  }

  applyCandidate(a: any) {
    this.selectedCandidate = a;
    this.myForm.patchValue({
      donated_by: a.donorName || '',
      mobile: this.last10(a.donorMobile),
      email: this.isTestRun ? this.testEmail : (a.email || ''),
      pan: (a.pan || '').trim().toUpperCase(),
      address: a.donoraddress || '',
      city: a.donorcity || '',
      state: a.state || '',
      zipcode: a.zipcode || '',
    });
    this.myForm.markAllAsTouched();
  }

  candidateLabel(a: any): string {
    return `${a.donorName} — ₹${a.amount} — ${a.date.toDate().toLocaleString('en-IN')}`;
  }

  private last10(value: any): string {
    return String(value || '').replace(/\D/g, '').slice(-10);
  }

  //* Receipts are numbered in the financial year (Apr–Mar) in which they are issued
  private receiptPrefix(): string {
    const today = new Date();
    const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return `${startYear}-${startYear + 1}-80G-ONL-`;
  }

  private formatReceiptId(counter: number): string {
    return this.receiptPrefix() + String(counter).padStart(3, '0');
  }

  private async previewReceiptId() {
    const counter = (await get(ref(this.mDatabase, this.env.receiptCounter))).val();
    this.nextReceiptId = counter ? this.formatReceiptId(Number(counter)) : '';
  }

  //* The counter holds the NEXT receipt number. Take it and bump it in one transaction
  //* so two people adding donations at the same time can never get the same receipt number.
  private async reserveReceiptNumber(): Promise<number> {
    let reserved = 0;
    const result = await runTransaction(ref(this.mDatabase, this.env.receiptCounter), current => {
      if (current === null) return current; // not loaded yet — the SDK retries with the server value
      reserved = Number(current);
      return reserved + 1;
    }, {applyLocally: false});
    if (!result.committed || !reserved) throw new Error('Could not reserve a receipt number');
    return reserved;
  }

  //* A payment is already in donations when its payment id or order id is stored on any donation
  private async findExistingDonation(): Promise<AddedDonation | null> {
    const col = this.env.donations;
    const lookups: [string, string][] = [['order_id', this.payment.id], ['payment_id', this.payment.id]];
    if (this.payment.order_id) lookups.push(['order_id', this.payment.order_id]);
    for (const [field, value] of lookups) {
      const snap = await firstValueFrom(this.mFirestore.collection(col, r => r.where(field, '==', value).limit(1)).get());
      if (!snap.empty) {
        return {id: snap.docs[0].id, receipt_id: (snap.docs[0].data() as any).receipt_id || ''};
      }
    }
    return null;
  }

  async submit() {
    if (this.myForm.invalid) {
      this.myForm.markAllAsTouched();
      this.snackBar.open('Please enter all fields', '', {panelClass: 'snackbar-error', duration: 3000});
      return;
    }
    this.isSaving = true;
    let receiptId = '';
    try {
      const existing = await this.findExistingDonation();
      if (existing) {
        this.snackBar.open(`Already in donations (${existing.receipt_id})`, '', {duration: 4000});
        this.dialogRef.close(existing);
        return;
      }

      receiptId = this.formatReceiptId(await this.reserveReceiptNumber());
      const form = this.myForm.getRawValue();
      //* Same shape the donation website writes on a successful payment
      const donation = {
        ...form,
        addtax: false,
        tax: 0,
        details: this.payment,
        method: this.payment.method,
        donation_amount: this.amount,
        totalAmountWithoutTax: this.amount,
        order_id: this.payment.order_id || this.payment.id,
        payment_id: this.payment.id,
        receipt_id: receiptId,
        createdAt: this.paymentDate.getTime(),
        date: this.paymentDate,
        addedFromRazorpay: true,
        addedAt: new Date(),
        attemptedDonationId: this.selectedCandidate?.id || null,
      };
      // Payment id as the doc id, so the same payment can never be added twice
      await this.mFirestore.collection(this.env.donations).doc(this.payment.id).set(donation);

      this.mLogService.addLog({
        date: new Date().getTime(),
        section: 'Donation',
        action: 'Submit',
        description: `Razorpay payment ${this.payment.id} added to donations with receipt ${receiptId}`,
        currentIp: localStorage.getItem('currentip')!
      });
      this.snackBar.open(`Added to donations — receipt ${receiptId}`, '', {duration: 4000});
      this.dialogRef.close({id: this.payment.id, receipt_id: receiptId});
    } catch (e) {
      console.error('Error adding donation:', e);
      const reserved = receiptId ? ` Receipt number ${receiptId} was reserved but not used.` : '';
      this.snackBar.open('Failed to add donation.' + reserved, 'OK', {panelClass: 'snackbar-error'});
    } finally {
      this.isSaving = false;
    }
  }
}
